export function sanitizeEvidenceName(rawName) {
  if (!rawName || typeof rawName !== 'string') return "Documento de Prova";

  let clean = rawName.replace(/\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|gif|bmp|tiff?|txt|rtf|csv|zip|rar|7z|msg|eml)$/i, '').trim();
  const prefixRegex = /^\s*(?:ANEXO|DOC|DOCUMENTO)S?\s*[\d]*\s*[-_.:\|]?\s*/i;
  clean = clean.replace(prefixRegex, '');

  clean = clean
    .replace(/[_]/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (clean === "") clean = "Documento Sem Título";

  clean = clean.split(' ').map(word => {
    if (word.length <= 2) return word.toLowerCase();
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');

  return clean;
}

export function formatCPF(value) {
  const v = String(value ?? '').replace(/\D/g, '').slice(0, 11);
  if (!v) return '';
  return v
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export function formatCNPJ(value) {
  const v = String(value ?? '').replace(/\D/g, '').slice(0, 14);
  if (!v) return '';
  return v
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function formatPhone(value) {
  const r = String(value ?? '').replace(/\D/g, '').slice(0, 11);
  if (!r) return '';
  if (r.length > 10) return r.replace(/^(\d\d)(\d{5})(\d{4}).*/, '($1) $2-$3');
  if (r.length > 5) return r.replace(/^(\d\d)(\d{4})(\d{0,4}).*/, '($1) $2-$3');
  if (r.length > 2) return r.replace(/^(\d\d)(\d{0,5}).*/, '($1) $2');
  return r;
}

export function formatCEP(value) {
  const v = String(value ?? '').replace(/\D/g, '').slice(0, 8);
  if (!v) return '';
  return v.replace(/^(\d{5})(\d)/, '$1-$2');
}

export function formatRG(value) {
  const v = String(value ?? '').replace(/\D/g, '').slice(0, 9);
  if (!v) return '';
  if (v.length <= 2) return v;
  if (v.length <= 5) return v.replace(/^(\d{2})(\d+)/, '$1.$2');
  if (v.length <= 8) return v.replace(/^(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
  return v.replace(/^(\d{2})(\d{3})(\d{3})(\d)$/, '$1.$2.$3-$4');
}

export function formatNPU(value) {
  const raw = String(value ?? '').replace(/\D/g, '').slice(0, 20);
  if (!raw) return '';
  const n = raw;
  if (n.length <= 7)  return n;
  if (n.length <= 9)  return `${n.slice(0,7)}-${n.slice(7)}`;
  if (n.length <= 13) return `${n.slice(0,7)}-${n.slice(7,9)}.${n.slice(9)}`;
  if (n.length <= 14) return `${n.slice(0,7)}-${n.slice(7,9)}.${n.slice(9,13)}.${n.slice(13)}`;
  if (n.length <= 16) return `${n.slice(0,7)}-${n.slice(7,9)}.${n.slice(9,13)}.${n.slice(13,14)}.${n.slice(14)}`;
  if (n.length <= 20) return `${n.slice(0,7)}-${n.slice(7,9)}.${n.slice(9,13)}.${n.slice(13,14)}.${n.slice(14,16)}.${n.slice(16)}`;
  return `${n.slice(0,7)}-${n.slice(7,9)}.${n.slice(9,13)}.${n.slice(13,14)}.${n.slice(14,16)}.${n.slice(16,20)}`;
}

const _NPU_REGEX = /^\d{7}-\d{2}\.\d{4}\.\d{1}\.\d{2}\.\d{4}$/;

export function isValidNPU(value) {
  const formatted = formatNPU(String(value ?? ''));
  return _NPU_REGEX.test(formatted);
}

// MC101 (11/09/2026): achado do titular — nome de cliente/parte aparece
// em CAIXA ALTA numa tela e em Nome Próprio na tela ao lado, porque não
// existe padronização nenhuma (o valor exibido é exatamente como foi
// digitado no cadastro, em qualquer caixa). Pesquisa (tribunal, mercado de
// software jurídico, acessibilidade) apontou pra Nome Próprio Capitalizado
// como o padrão de EXIBIÇÃO — decisão do titular, registrada no artefato
// "Anatomia da Maiúscula" (11/09/2026): (1) o motor de qualificação
// jurídica dos documentos (ClientSchema.js) continua em maiúscula, não
// mexe aqui; (2) só a exibição em tela muda, o dado gravado no Firestore
// fica como está; (3) o cadastro (ClientManager.jsx) para de forçar
// maiúscula ao salvar. Esta função cobre o item (2) — é aplicada na hora
// de EXIBIR, nunca no dado gravado.
const _TITLE_CASE_LOWERCASE_PT = new Set([
  'de', 'da', 'do', 'das', 'dos', 'e', 'vs', 'vs.',
  // MC102 (12/09/2026): título de caderno Pré-Processual às vezes é nome de
  // pessoa ("[CLIENTE_D]"), às vezes é descrição livre do caso
  // ("Ressarcimento de Custos com Manutenção dos Animais", "Prisão em
  // Flagrante") — a mesma função passou a formatar os dois (achado do
  // titular: nome e Cliente ficavam em caixas diferentes na mesma linha).
  // Preposições comuns em descrição (nunca aparecem em nome de pessoa)
  // ficam minúsculas pra não estragar frases que já estavam certas.
  'com', 'em', 'para', 'por', 'sem', 'ou',
]);
const _TITLE_CASE_KEEP_AS_IS_PT = {
  sa: 'SA', 's.a': 'S.A', 's.a.': 'S.A.',
  ltda: 'Ltda', 'ltda.': 'Ltda.',
  me: 'ME', epp: 'EPP', eireli: 'EIRELI',
  cnpj: 'CNPJ', cpf: 'CPF', rg: 'RG', oab: 'OAB',
};

export function toTitleCasePt(value) {
  const str = String(value ?? '').trim();
  if (!str) return '';
  return str
    .split(' ')
    .filter(Boolean)
    .map((rawWord, idx) => {
      const lower = rawWord.toLowerCase();
      if (_TITLE_CASE_KEEP_AS_IS_PT[lower]) return _TITLE_CASE_KEEP_AS_IS_PT[lower];
      if (idx > 0 && _TITLE_CASE_LOWERCASE_PT.has(lower)) return lower;
      return lower.replace(/(^|[-(])\p{L}/gu, (m) => m.toUpperCase());
    })
    .join(' ');
}

// Identidade do processo (padrão Astrea: cada processo/caderno é uma
// entidade única, titulada pela relação entre as partes + CNJ, não pelo
// apelido interno). Distinto de `resolveCadernoLabel` (CadernoCockpit.jsx),
// que prioriza o apelido de propósito — aquele alimenta o dropdown interno
// de troca rápida de caderno, este alimenta a lista global "Processos e
// Casos", que segue a convenção de mercado validada contra a Astrea.
export function resolveProcessTitle(workspace) {
  const w = workspace || {};
  const clientName = String(w.clientName || '').trim() || 'Cliente não identificado';
  const numeroCNJ = String(w.numeroCNJ || '').trim();
  const adversas = Array.isArray(w?.partes?.adversas) ? w.partes.adversas : [];

  // Título correto vem do polo real (Polo Ativo vs. Polo Passivo), nunca da
  // posição no array — em processos com 3+ partes (ex.: Inquérito Policial
  // com autoridade + indiciado + terceiro interessado), `adversas[0]` pode
  // ser a autoridade (Ministério Público), não um adversário do cliente.
  // Achado real (23/08/2026, caso [PET_A]): título saía "[PET_A] vs.
  // Ministério Público" porque o MP era o índice 0, quando na verdade o
  // processo é Ministério Público (Polo Ativo) vs. o indiciado (Polo
  // Passivo) — [PET_A] é terceiro interessado, não parte do "vs.".
  const poloAtivo = adversas.find((p) => /ativo/i.test(String(p?.polo || '')));
  const poloPassivo = adversas.find((p) => /passivo/i.test(String(p?.polo || '')));
  const advName = adversas.length > 0 ? String(adversas[0]?.nome || '').trim() : '';

  // Achado real (23/08/2026, caso [PET_A]/Mandado de Segurança): quando o
  // Polo Ativo é a própria parte representada (ex.: tutora entrando com
  // Mandado de Segurança), ela mora em `partiesRefs` (papel do cliente),
  // não em `partes.adversas` (só partes de fora) — sem isso, o título caía
  // no fallback genérico e virava "[PET_A] vs. Estado de Goiás" (nome do
  // cliente-caso, não da pessoa que realmente processa).
  const partiesRefs = Array.isArray(w?.partiesRefs) ? w.partiesRefs : [];
  const poloAtivoRef = partiesRefs.find((p) => /ativo/i.test(String(p?.polo || '')));

  if (numeroCNJ) {
    // Resolvido uma vez só aqui — quem precisa dos nomes separados por polo
    // (ex.: card de Partes em ProcessosCadernoListView) usa
    // poloAtivoNome/poloPassivoNome do retorno, em vez de recalcular por
    // conta própria (2 cópias da mesma lógica já haviam divergido: uma
    // tinha o fallback de partiesRefs, a outra não).
    let names = clientName;
    let poloAtivoNome = '';
    let poloPassivoNome = '';
    if (poloAtivo?.nome && poloPassivo?.nome) {
      poloAtivoNome = String(poloAtivo.nome).trim();
      poloPassivoNome = String(poloPassivo.nome).trim();
      names = `${poloAtivoNome} vs. ${poloPassivoNome}`;
    } else if (poloAtivoRef?.name && poloPassivo?.nome) {
      poloAtivoNome = String(poloAtivoRef.name).trim();
      poloPassivoNome = String(poloPassivo.nome).trim();
      names = `${poloAtivoNome} vs. ${poloPassivoNome}`;
    } else if (advName) {
      names = `${clientName} vs. ${advName}`;
    }
    // Convenção 23/08/2026 (pedido do titular): número do CNJ sempre junto
    // no título ("CNJ - nomes"), igual ao que já acontecia no ramo
    // pré-processual (alias) — antes o CNJ só aparecia separado, como
    // subtítulo, deixando processo com CNJ real e caderno pré-processual
    // com cara diferente um do outro.
    // MC101: só o "nome das partes" passa por Nome Próprio — o número (já
    // formatado por `formatNPU`) não tem letra, então o transform não o
    // afeta, mas aplicar a `names` isolado (antes de concatenar) deixa
    // isso explícito em vez de acidental. `poloAtivoNome`/`poloPassivoNome`
    // no retorno continuam BRUTOS (sem transform) — outros consumidores já
    // existentes desses 2 campos (ex.: card de Partes) podem depender do
    // valor exato gravado, não é seguro reescrever por baixo deles aqui.
    return {
      title: `${formatNPU(numeroCNJ)} - ${toTitleCasePt(names)}`,
      subtitle: '',
      isPreProcessual: false,
      numeroFormatted: formatNPU(numeroCNJ),
      poloAtivoNome,
      poloPassivoNome,
    };
  }

  // Sem CNJ ainda (pré-processual): o apelido é a única coisa que distingue
  // um caderno do outro do mesmo cliente (ex.: [PET_A] tem 7) - cair direto
  // no genérico "Cliente (Pré-Processual)" sem checar o apelido primeiro
  // tornava todos eles com o mesmo título. Achado testando a tela real.
  // MC102 (12/09/2026): achado do titular — este título ficava de fora do
  // `toTitleCasePt` (eu tratava como "descrição livre do caso", não nome),
  // mas na prática o alias quase sempre É o nome de uma pessoa
  // ("[CLIENTE_D] (Pré-Processual)") — ficava em caixa diferente da
  // coluna Cliente na MESMA linha. `toTitleCasePt` também formata bem
  // descrição de verdade (ver preposições extras acima), então aplicar
  // aqui resolve os dois casos sem precisar distinguir um do outro.
  const alias = String(w.alias || '').trim();
  if (alias) {
    return { title: toTitleCasePt(alias), subtitle: 'Pré-Processual', isPreProcessual: true };
  }

  const preProcNote = String(w?.ancestry?.pre_processual_note || '').trim();
  if (preProcNote) {
    return { title: toTitleCasePt(preProcNote), subtitle: 'Pré-Processual', isPreProcessual: true };
  }
  return {
    title: `${toTitleCasePt(clientName)} (Pré-Processual)`,
    subtitle: 'Pré-Processual',
    isPreProcessual: true,
  };
}

// MC79 (10/09/2026): extraído do diagrama da aba Vínculos (CadernoCockpit.jsx,
// bloco activeTab==="vinculos", MC62) pra ser reaproveitado também na árvore
// lateral de Processos — mesma cor por status nos dois lugares, sem duplicar
// a lógica (mesmo motivo do MC51 abaixo). Cor semântica: Ativo=verde,
// Suspenso/Liminar=alerta, Recurso=azul (em andamento), Arquivado=cinza
// esmaecido — nunca só a cor sozinha, sempre com o nome do status por
// extenso ao lado (Art. acessibilidade, CLAUDE.md).
export const STATUS_STYLES = {
  ativo: { badge: 'bg-green-100 text-green-700', dot: 'bg-green-500', border: 'border-green-500', bg: 'bg-green-50' },
  suspenso: { badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', border: 'border-amber-500', bg: 'bg-amber-50' },
  recurso: { badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500', border: 'border-blue-500', bg: 'bg-blue-50' },
  liminar: { badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', border: 'border-orange-500', bg: 'bg-orange-50' },
  // MC104j (12/09/2026): achado do titular — a borda de "arquivado" usava
  // gray-300 (tom claro) enquanto as outras 4 usam a faixa 500 da própria
  // cor (green-500/amber-500/blue-500/orange-500) — não é diferença de
  // espessura, é a MESMA borda (`border`, 1px) com saturação bem mais
  // fraca, o que lia como "linha fina" ao lado das outras. Trocado pra
  // gray-500, mesma faixa de intensidade das demais.
  arquivado: { badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400', border: 'border-gray-500', bg: 'bg-gray-50' },
};
export const DEFAULT_STATUS_STYLE = { badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400', border: 'border-gray-300', bg: 'bg-gray-50' };
export function getStatusStyle(statusProcesso) {
  const raw = String(statusProcesso || '').trim().toLowerCase();
  if (STATUS_STYLES[raw]) return STATUS_STYLES[raw];
  // MC104p (15/09/2026, achado ao vivo — Chrome real + CDP): titular
  // relatou bordas "de espessura/formato diferente" na árvore Por
  // Vínculos — medindo o DOM real (`getComputedStyle`), TODA borda já é
  // 1px sólido, idêntica; a diferença é só de CONTRASTE (border-500
  // saturado ao lado de border-300 apagado, mesmo efeito do MC104j, só
  // que incompleto). Causa raiz: `status` no Firestore não é sempre a
  // palavra isolada ("Arquivado") — muitos registros guardam
  // "Arquivado - DEFERIMENTO DO PEDIDO..."/"- LITISPENDÊNCIA" (motivo
  // anexado ao status). O match exato falhava pra QUALQUER um desses,
  // caindo no DEFAULT_STATUS_STYLE (border-gray-300, apagado) mesmo
  // sendo genuinamente arquivado. Normaliza pegando só a palavra ANTES do
  // primeiro " - " antes de tentar de novo.
  const prefix = raw.split(/\s+-\s+/)[0]?.trim();
  if (prefix && STATUS_STYLES[prefix]) return STATUS_STYLES[prefix];
  return DEFAULT_STATUS_STYLE;
}

// MC51 (Fase F2b): extraídas de ProcessosCadernoListView.jsx pra serem
// compartilhadas com o ProcessPicker (busca de vínculo) - mesma lógica de
// busca em 2 lugares, sem duplicar (mesmo motivo da extração do FlyoutPanel
// - 2 implementações que já haviam divergido antes de precisarem ser
// reaproveitadas de novo).
export function normalizeSearchText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

export function cleanCNJDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

export function filterWorkspacesBySearch(workspaces, search) {
  const list = Array.isArray(workspaces) ? workspaces : [];
  const term = normalizeSearchText(search);
  const termDigits = cleanCNJDigits(search);
  if (!term && !termDigits) return list;
  return list.filter((w) => {
    const alias = normalizeSearchText(w?.alias);
    const clientName = normalizeSearchText(w?.clientName);
    const cnjDigits = cleanCNJDigits(w?.numeroCNJ);
    const matchesText = term && (alias.includes(term) || clientName.includes(term));
    const matchesCNJ = termDigits && cnjDigits.includes(termDigits);
    return matchesText || matchesCNJ;
  });
}